"use client";

/**
 * Schedule Countdown
 *
 * A live digital-clock countdown ("02d : 05h : 20m : 11s") to a target instant, used by the
 * pre-event live banner. It ticks locally at second resolution via `useClientNow`, so only this
 * node re-renders each second — the parent banner keeps its minute clock for live-status math.
 * Once the target passes it shows "Starting now" until the parent's next minute tick flips the
 * banner into its live state.
 */

import type React from "react";

import { useClientNow } from "../../../../_hooks/useNow";
import { COUNTDOWN_TICK_MS } from "../../../../_logic/constants";
import { formatCountdown } from "../../../../_logic/utils/schedule-utils";

interface ScheduleCountdownProps {
  /** Target instant (ms since epoch, UTC) to count down to. */
  targetMs: number;
}

/**
 * Render a second-resolution countdown to `targetMs`.
 *
 * @param props - See {@link ScheduleCountdownProps}.
 * @returns The countdown element, or `null` before the component has mounted on the client
 *   (matches `useClientNow`'s hydration-safe contract).
 */
export function ScheduleCountdown({ targetMs }: ScheduleCountdownProps): React.ReactElement | null {
  const now = useClientNow(COUNTDOWN_TICK_MS);
  if (now == null) return null;

  const remaining = targetMs - now;
  // aria-live is off: a per-second update would flood screen readers. The banner is supplementary.
  return (
    <span className="font-semibold tabular-nums tracking-wide" aria-live="off">
      {remaining > 0 ? formatCountdown(remaining) : "Starting now"}
    </span>
  );
}
